import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import { MachineActions } from 'redux/actions/settings/MachineActions';
import { MachineMaintenanceActions } from 'redux/actions/settings/MachineMaintenanceActions';
import { MaintenanceActions } from 'redux/actions/settings/MaintenanceActions';

import { TableColumn, Table, TableRow } from 'app/components/common/Table';
import { StoreModel } from 'models/StoreModel';
import { LoginUser } from 'models/LoginUser';
import { Machine } from 'models/Machine';
import { MachineMaintenance } from 'models/MachineMaintenance';
import { Maintenance } from 'models/Maintenance';
import { PageContentHeader } from 'app/components/common/PageContentHeader';
import { Translations } from 'models/Translations';
import Translator from 'app/components/common/Translator';
import { MachineType } from 'models/MachineType';
import { MachineTypeActions } from 'redux/actions/settings/MachineTypeActions';
import { MachinePartActions } from 'redux/actions/settings/MachinePartActions';
import { MachinePart } from 'models/MachinePart';
import { formatDays } from 'infrastructure/duration';
import { Translate } from 'infrastructure/mapping/Translate';

interface Props {
    fetchMachineMaintenances: typeof MachineMaintenanceActions.fetchMachineMaintenances;
    fetchMachines: typeof MachineActions.fetchMachines;
    fetchMachineTypes: typeof MachineTypeActions.fetchMachineTypes;
    fetchMaintenances: typeof MaintenanceActions.fetchMaintenances;
    fetchMachineParts: typeof MachinePartActions.fetchMachineParts;
    moveToAddMachineMaintenance: typeof MachineMaintenanceActions.moveToAddMachineMaintenance;
    moveToEditMachineMaintenance: typeof MachineMaintenanceActions.moveToEditMachineMaintenance;
    deleteMachineMaintenance: typeof MachineMaintenanceActions.deleteMachineMaintenance
    loginUser: LoginUser;
    machineMaintenances: MachineMaintenance[];
    machines: Machine[];
    machineTypes: MachineType[];
    maintenances: Maintenance[];
    machineParts: MachinePart[];
    translations: Translations;
}

class MachineMaintenances extends React.Component<Props> {
    componentDidMount() {
        if (this.loggedIn()) {
            if (!this.props.machineMaintenances) {
                this.props.fetchMachineMaintenances(this.props.loginUser.token);
            }
            if (!this.props.machines) {
                this.props.fetchMachines(this.props.loginUser.token);
            }
            if (!this.props.machineTypes) {
                this.props.fetchMachineTypes(this.props.loginUser.token);
            }
            if (!this.props.maintenances) {
                this.props.fetchMaintenances(this.props.loginUser.token);
            }
            if (!this.props.machineParts) {
                this.props.fetchMachineParts(this.props.loginUser.token);
            }
        }
    }

    loggedIn() {
        return this.props.loginUser && this.props.loginUser.token;
    }

    ready() {
        return this.loggedIn() && this.props.machineMaintenances && this.props.machines && this.props.maintenances
    }

    partNames(mm: MachineMaintenance) {
        // a freshly saved part may only carry its id
        return (mm.parts ?? []).map(p => (this.props.machineParts?.find(mp => mp.id === p.machinePart.id) ?? p.machinePart).name + ' (' + p.quantity + ')').join(', ');
    }

    setMachineData(): TableRow[] {
        let data: TableRow[] = [];
        {
            this.props.machineMaintenances.forEach(machineMaintenance => {
                data.push({
                    dataObject: {
                        id: machineMaintenance.id,
                        machine: machineMaintenance.machineType,
                        maintenance: machineMaintenance.maintenance,
                        intervalDays: machineMaintenance.intervalDays,
                        warningThresholdDays: machineMaintenance.warningThresholdDays,
                        intervalServiceLife: machineMaintenance.intervalServiceLife,
                        warningThresholdServiceLife: machineMaintenance.warningThresholdServiceLife,
                        parts: machineMaintenance.parts,
                        archivedDate: machineMaintenance.archivedDate
                    },
                    cells: [
                        {
                            name: "parts",
                            sortVal: this.partNames(machineMaintenance),
                            cell: <>{this.partNames(machineMaintenance)}</>,
                        },
                        {
                            name: "intervalDays",
                            sortVal: machineMaintenance.intervalDays,
                            cell: <>{formatDays(machineMaintenance.intervalDays, t => Translate.text(this.props.translations, t))}</>,
                        },
                        {
                            name: "warningThresholdDays",
                            sortVal: machineMaintenance.warningThresholdDays,
                            cell: <>{formatDays(machineMaintenance.warningThresholdDays, t => Translate.text(this.props.translations, t))}</>,
                        },
                    ]
                })
            })
        }
        return data;
    }

    setColumns(): TableColumn[] {
        // TODO maybe dynamic??
        return [
            {
                header: 'Id',
                accessor: 'id',
            },
            {
                header: 'Machine',
                accessor: 'machine',
            },
            {
                header: 'Maintenance',
                accessor: 'maintenance',
            },
            {
                header: 'Time interval',
                accessor: 'intervalDays',
            },
            {
                header: 'Time before warning',
                accessor: 'warningThresholdDays',
            },
            {
                header: 'Service life interval',
                accessor: 'intervalServiceLife',
            },
            {
                header: 'Service life before warning',
                accessor: 'warningThresholdServiceLife',
            },
            {
                header: 'Machine Parts',
                accessor: 'parts',
            },
        ];
    }

    render() {
        if (!this.ready()) {
            return (
                <div className="c-page__content">
                    <PageContentHeader text="Machine Maintenance" />
                    <Translator text="Loading" />
                </div>
            )
        }
        return (
            <div className="c-page__content">
                <PageContentHeader text="Machine Maintenance" />
                <div className="c-card">
                    <Table
                        addAction={this.props.moveToAddMachineMaintenance}
                        editAction={this.props.moveToEditMachineMaintenance}
                        deleteAction={this.props.deleteMachineMaintenance}
                        editable={true}
                        columns={this.setColumns()}
                        tableData={this.setMachineData()}
                        translations={this.props.translations}
                        loginUser={this.props.loginUser}
                    />
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: StoreModel) {
    return {
        loginUser: state.loginUser,
        machines: state.machines,
        machineMaintenances: state.machineMaintenances,
        maintenances: state.maintenances,
        machineParts: state.machineParts,
        translations: state.translations
    };
}
function mapDispatchToProps(dispatch: Dispatch) {
    return {
        fetchMachineMaintenances: bindActionCreators(MachineMaintenanceActions.fetchMachineMaintenances, dispatch),
        fetchMachines: bindActionCreators(MachineActions.fetchMachines, dispatch),
        fetchMachineTypes: bindActionCreators(MachineTypeActions.fetchMachineTypes, dispatch),
        fetchMaintenances: bindActionCreators(MaintenanceActions.fetchMaintenances, dispatch),
        fetchMachineParts: bindActionCreators(MachinePartActions.fetchMachineParts, dispatch),
        moveToAddMachineMaintenance: bindActionCreators(MachineMaintenanceActions.moveToAddMachineMaintenance, dispatch),
        moveToEditMachineMaintenance: bindActionCreators(MachineMaintenanceActions.moveToEditMachineMaintenance, dispatch),
        deleteMachineMaintenance: bindActionCreators(MachineMaintenanceActions.deleteMachineMaintenance, dispatch),
    };
}
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(MachineMaintenances);